import {
  CampaignControllerCountCampaignsParams,
  CampaignControllerGetCampaignParams,
  CampaignControllerListCampaingsParams,
  CampaignItemDto
} from '@api/Api';
import { apiClient } from '@api/client';
import { getItemPlaceholderFromListQuery } from '@hooks/queries/helpers';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

export function useCampaignQuery(
  params: CampaignControllerGetCampaignParams,
  options?: {
    enabled?: boolean;
    keepPreviousData?: boolean;
    placeholderData?: CampaignItemDto;
  }
) {
  return useQuery({
    ...(options ?? {}),
    queryKey: ['campaigns', 'item', params.campaignId, params],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.campaign.campaignControllerGetCampaign(
        params,
        { signal }
      );
      return data.campaign;
    },
    placeholderData: () => {
      if (options?.placeholderData) return options.placeholderData;
      const campaign = getItemPlaceholderFromListQuery<CampaignItemDto>(
        ['campaigns', 'list'],
        params.campaignId
      );
      if (!campaign) return undefined;
      return campaign;
    }
  });
}

export function useCampaignsQuery(
  params: CampaignControllerListCampaingsParams,
  options?: {
    enabled?: boolean;
    keepPreviousData?: boolean;
  }
) {
  return useInfiniteQuery({
    ...(options ?? {}),
    queryKey: ['campaigns', 'list', params],
    queryFn: async ({ pageParam, signal }) => {
      const { data } = await apiClient.campaign.campaignControllerListCampaings(
        { ...params, after: pageParam },
        { signal }
      );
      return data;
    },
    getNextPageParam: (lastPage) =>
      lastPage.hasNext ? lastPage.endCursor : undefined
  });
}

export function useCampaignsCountQuery(
  params: CampaignControllerCountCampaignsParams,
  options?: {
    enabled?: boolean;
    keepPreviousData?: boolean;
    placeholderData?: number;
  }
) {
  return useQuery({
    ...(options ?? {}),
    queryKey: ['campaigns', 'count', params],
    queryFn: async ({ signal }) => {
      const { data } =
        await apiClient.campaign.campaignControllerCountCampaigns(params, {
          signal
        });
      return data.count;
    }
  });
}
